import { ref, computed } from "vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import { FETCH_DEMO_UNITS, CREATE_DEMO_UNITS } from "./graphql";
import { addOrganizationDevices } from "../../graphql/organizations/index";
import { ADD_UNIT_RESPONDERS, REMOVE_UNIT_RESPONDER } from "../renters/graphql";
import useInventory from "../smart_units/useInventory";
import {
  deviceLastMotion,
  extractDevicesFields,
  formatDeviceName,
} from "../common/helpers/devices";
import { formatPhoneNumber } from "@tod-ui/helpers/phone";
import useBillingAccount from "../renters/useBillingAccount";
import { UPDATE_BILLING_ACCOUNT } from "../renters/graphql";
import {
  createEosForm,
  userBillingAccounts,
  organizationUsersReport,
} from "../../graphql/users";
import useRenter from "../renters/useRenter";

export function useDemoUnits(facilityId) {
  const { addAlert } = useAlerts();

  const demoUnits = ref([]);
  const demoUnitsEnabled = ref(false);
  const loading = ref(false);
  const toggleLoading = ref(false);
  const addLoading = ref(false);
  const assignLoading = ref(false);
  const responderLoading = ref(false);

  const { availableInventory, fetchInventory, healthyFacilityDevices } =
    useInventory();

  // Skeleton placeholder rows shown while real data is loading
  const mockItems = Array.from({ length: 3 }, (_, index) => ({
    id: `mock-${index + 1}`,
    name: `Demo ${index + 1}`,
    managerPhone: "",
    isMock: true,
    devices: [],
    responders: [],
  }));

  const availableDevices = computed(() => {
    const devices =
      healthyFacilityDevices.value?.length > 0
        ? healthyFacilityDevices.value
        : availableInventory.value.filter((item) => item.type === "device");

    return devices.filter(
      (device) =>
        device.disposition === "in_service" &&
        device.status === "ok" &&
        !device.rmaRequest
    );
  });

  const formatPhoneForDisplay = (responders) => {
    if (!responders?.length) return "";

    const phoneResponder = responders.find(
      (r) => r.contactMethod?.type === "mobile"
    );

    return phoneResponder
      ? formatPhoneNumber(phoneResponder.contactMethod.value)
      : "";
  };

  const processUnitWithDeviceFields = (unit) => {
    const devices = unit.devices || [];

    return {
      ...unit,
      managerPhone: formatPhoneForDisplay(unit.responders),
      devices,
      responders: unit.responders || [],
      devicesName: devices.map((device) => formatDeviceName(device)),
      devicesLastMotion: devices.map((device) => deviceLastMotion(device)),
      devicesHealth: devices,
      ...extractDevicesFields(devices),
    };
  };

  const findUnitByDeviceId = (deviceId) =>
    demoUnits.value.find((unit) =>
      unit.devices?.some((device) => device.id === deviceId)
    );

  const createDemoUser = async (apolloClient, phoneNumber) => {
    if (!phoneNumber) throw new Error("Phone number is required");

    const {
      initRenter,
      createRenter,
      checkRenter,
      fetchRenter,
      renter,
      findContactMethod,
    } = useRenter();

    initRenter("Demo", "Manager", phoneNumber);
    renter.value.username = phoneNumber;
    renter.value.usernameType = "phone";

    try {
      const validation = await checkRenter(apolloClient);

      if (!validation.valid) {
        throw new Error(`${phoneNumber} is not a valid phone number`);
      }

      if (!validation.available) {
        try {
          const existingUser = await fetchRenter(
            apolloClient,
            validation.matchingUserId,
            false
          );

          const primaryContact = findContactMethod(
            existingUser?.contactMethods,
            "mobile",
            true
          );

          if (!existingUser?.externalId && primaryContact) {
            return await handleExistingUser(apolloClient, existingUser);
          }

          generateUniqueEmailUsername(renter);
        } catch (error) {
          console.error(
            "Failed to fetch existing user, falling back to new renter:",
            error
          );
          generateUniqueEmailUsername(renter);
          return await createRenter(apolloClient, true);
        }
      }

      return await createRenter(apolloClient, true);
    } catch (error) {
      addAlert({
        type: "error",
        message: "Error creating demo user",
        timeout: 10,
      });
      throw error;
    }
  };

  const handleExistingUser = async (apolloClient, existingUser) => {
    const { assignUser, fetchUserBillingAccounts } = useBillingAccount();

    const billingAccountsList = await fetchUserBillingAccounts(
      apolloClient,
      existingUser.id,
      "network-only"
    );

    const hasActiveAccount = billingAccountsList.some(
      (bau) => bau.billingAccount.status === "active"
    );

    if (!hasActiveAccount) {
      await assignUser(apolloClient, existingUser, false);
    }

    return existingUser;
  };

  const generateUniqueEmailUsername = (renter) => {
    renter.value.usernameType = "email";
    renter.value.username = [
      Math.random().toString(36).substring(2, 15),
      Math.random().toString(36).substring(2, 15),
      "@storage-defender.com",
    ].join("");
  };

  const fetchDemoUnits = async (apolloClient) => {
    if (!facilityId || !apolloClient) return;

    loading.value = true;
    try {
      const { data } = await apolloClient.query({
        query: FETCH_DEMO_UNITS,
        variables: { parentId: facilityId },
        fetchPolicy: "network-only",
      });

      if (data?.fetchDemoUnits) {
        demoUnits.value = data.fetchDemoUnits.map(processUnitWithDeviceFields);
        demoUnitsEnabled.value = demoUnits.value.length > 0;
      } else {
        demoUnits.value = [];
        demoUnitsEnabled.value = false;
      }
    } catch (error) {
      addAlert({
        type: "error",
        message: "Error fetching demo units",
        timeout: 10,
      });

      demoUnits.value = [];
      demoUnitsEnabled.value = false;
    } finally {
      loading.value = false;
    }
  };

  const createDemoUnits = async (apolloClient) => {
    if (!facilityId || !apolloClient) return;

    try {
      const { data } = await apolloClient.mutate({
        mutation: CREATE_DEMO_UNITS,
        variables: { parentId: facilityId },
      });

      if (data?.createDemoUnits) {
        await fetchDemoUnits(apolloClient);
      }
    } catch (error) {
      addAlert({
        type: "error",
        message: "Failed to create demo units",
        timeout: 10,
      });
    }
  };

  const assignDeviceToUnit = async (apolloClient, unitId, deviceId) => {
    if (!apolloClient || !unitId || !deviceId) return;

    assignLoading.value = true;
    try {
      await apolloClient.mutate({
        mutation: addOrganizationDevices,
        variables: { devices: [deviceId], id: unitId },
      });

      // Refresh data
      await Promise.all([
        fetchDemoUnits(apolloClient),
        fetchInventory(apolloClient),
      ]);

      return "Device assigned successfully";
    } catch (error) {
      addAlert({
        type: "error",
        message: "Unable to assign device to demo unit",
        timeout: 10,
      });
    } finally {
      assignLoading.value = false;
    }
  };

  const removeDeviceFromUnit = async (apolloClient, facilityId, deviceId) => {
    if (!apolloClient || !facilityId || !deviceId) return;

    const unit = findUnitByDeviceId(deviceId);

    if (unit) {
      await removeUnitResponders(apolloClient, unit);
    }

    // Move device back to facility
    await apolloClient.mutate({
      mutation: addOrganizationDevices,
      variables: { devices: [deviceId], id: facilityId },
    });

    // Refresh data
    await Promise.all([
      fetchDemoUnits(apolloClient),
      fetchInventory(apolloClient),
    ]);

    return "Device and all responders removed successfully";
  };

  // Cleanup responders and billing accounts
  const removeUnitResponders = async (apolloClient, unit) => {
    const responders = unit.responders || [];

    for (const responder of responders) {
      try {
        await cleanupResponderBilling(apolloClient, responder);
        await removeResponder(apolloClient, responder, unit.id);
      } catch (error) {
        addAlert({
          type: "error",
          message: `Error removing responder ${responder.id}:`,
          timeout: 10,
        });
        throw error;
      }
    }
  };

  // cleanup billing account
  const cleanupResponderBilling = async (apolloClient, responder) => {
    const { data } = await apolloClient.query({
      query: userBillingAccounts,
      variables: {
        userId: responder.user.id,
        parentOrganizationId: facilityId,
      },
    });

    const userBillingAccount = data.userBillingAccounts?.find(
      (account) => account.billingAccount.type === "consumer"
    );

    if (userBillingAccount) {
      const billingAccount = userBillingAccount.billingAccount;

      // Create EOS form
      await apolloClient.mutate({
        mutation: createEosForm,
        variables: {
          reason: "Demo unit cleanup",
          comments: "Demo unit device removed",
          billingAccountId: billingAccount.id,
          organizationId: facilityId,
        },
      });

      // Close billing account
      await apolloClient.mutate({
        mutation: UPDATE_BILLING_ACCOUNT,
        variables: {
          ...billingAccount,
          status: "closed",
        },
        refetchQueries: [
          {
            query: organizationUsersReport,
            variables: { id: facilityId },
          },
        ],
      });
    }
  };

  const removeResponder = async (apolloClient, responder, unitId) => {
    await apolloClient.mutate({
      mutation: REMOVE_UNIT_RESPONDER,
      variables: {
        id: responder.id,
        organizationId: unitId,
      },
    });
  };

  const addManagerPhoneWithUser = async (apolloClient, unitId, user) => {
    if (!apolloClient || !unitId || !user) return;

    const { findContactMethod } = useRenter();
    const primaryContact = findContactMethod(
      user.contactMethods,
      "mobile",
      true
    );

    if (!primaryContact) {
      throw new Error("User is missing a Primary Mobile Phone");
    }

    const responder = {
      userId: user.id,
      contactMethodId: primaryContact.id,
      escalationLevel: 1,
      demo: true,
      incidentTypes: ["motion", "entry"],
      accessHours: "all",
    };

    await apolloClient.mutate({
      mutation: ADD_UNIT_RESPONDERS,
      variables: { id: unitId, responders: [responder] },
    });

    await fetchDemoUnits(apolloClient);
    return "Manager phone assigned successfully";
  };

  const addManagerPhone = async (apolloClient, unitId, phoneNumber) => {
    if (!apolloClient || !unitId || !phoneNumber) return;

    responderLoading.value = true;
    try {
      const user = await createDemoUser(apolloClient, phoneNumber);
      return await addManagerPhoneWithUser(apolloClient, unitId, user);
    } finally {
      responderLoading.value = false;
    }
  };

  const removeManagerPhone = async (apolloClient, unitId) => {
    if (!apolloClient || !unitId) return;

    responderLoading.value = true;
    try {
      const unit = demoUnits.value.find((u) => u.id === unitId);
      if (unit) {
        await removeUnitResponders(apolloClient, unit);
        await fetchDemoUnits(apolloClient);
      }
      return "Manager phone removed successfully";
    } finally {
      responderLoading.value = false;
    }
  };

  const handleToggleChange = async (apolloClient, enabled) => {
    if (!apolloClient) return;

    toggleLoading.value = true;
    try {
      if (enabled) {
        await createDemoUnits(apolloClient);
        await fetchInventory(apolloClient);
      }
    } catch (error) {
      demoUnitsEnabled.value = !enabled;
      throw error;
    } finally {
      toggleLoading.value = false;
    }
  };

  const handleAddMore = async (apolloClient) => {
    if (!apolloClient) return;

    addLoading.value = true;
    try {
      await createDemoUnits(apolloClient);
    } catch (error) {
      addAlert({
        type: "error",
        message: `Error in Adding Demo Units :`,
        timeout: 10,
      });
    } finally {
      addLoading.value = false;
    }
  };

  return {
    demoUnits,
    demoUnitsEnabled,
    loading,
    toggleLoading,
    addLoading,
    assignLoading,
    responderLoading,
    mockItems,
    availableDevices,

    fetchDemoUnits,
    handleToggleChange,
    handleAddMore,
    assignDeviceToUnit,
    removeDeviceFromUnit,
    addManagerPhone,
    addManagerPhoneWithUser,
    removeManagerPhone,
    fetchInventory,
  };
}
